import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AssistantAttachment from "ai-assistant/attachments/assistant-attachment";
import AssistantAttachmentSchema from "ai-assistant/attachments/assistant-attachment-schema";
import AssistantCitation from "ai-assistant/citations/assistant-citation";
import AssistantCitationSchema from "ai-assistant/citations/assistant-citation-schema";
import AnswerFeedback from "ai-assistant/feedback/answer-feedback";
import AnswerFeedbackSchema from "ai-assistant/feedback/answer-feedback-schema";
import AssistantTurnStep from "ai-assistant/turn-steps/assistant-turn-step";
import AssistantTurnStepSchema from "ai-assistant/turn-steps/assistant-turn-step-schema";
import { z } from "zod";

export type AssistantConversationTurnJSON = z.infer<typeof AssistantConversationSchema.AssistantConversationTurnJSONZ>;
export type AssistantConversationTurnParams = z.infer<typeof AssistantConversationSchema.AssistantConversationTurnZ>;
export type AssistantConversationJSON = z.infer<typeof AssistantConversationSchema.AssistantConversationJSONZ>;
export type AssistantConversationParams = z.infer<typeof AssistantConversationSchema.AssistantConversationZ>;

/**
 * A conversation read back from the server.
 *
 * The transcript used to live in this component's React state and nowhere the user could reach,
 * so a refresh lost it while the server kept every turn indefinitely. See #4658.
 *
 * The citations arriving here are not the citations that were sent when the answer was written:
 * the server re-checks each one against what the reader may see now, and one they may no longer
 * read arrives with `withheld: true` and no text.
 */
export default class AssistantConversationSchema {
  public static readonly AssistantConversationTurnJSONZ = z.object({
    sequence: z.number(),
    userMessage: z.string(),
    answerText: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    citations: z.array(AssistantCitationSchema.AssistantCitationJSONZ),
    // Absent on a turn recorded before the server kept it.
    traceId: z.string().nullish(),
    // Whether the reader has since lost a source this answer cited. The answer still arrives
    // word for word; the server no longer sends it back to the model (#5211). Absent on a reply
    // from an API that predates the field, which reads as false.
    sourceLost: z.boolean().nullish(),
    // The reader's own report on this answer, if they made one (#5204). Absent otherwise.
    feedback: AnswerFeedbackSchema.AnswerFeedbackJSONZ.nullish(),
    // Whether this answer was shown with a result table, which is not replayed (#6223). Absent on
    // a reply from an API that predates the field, which reads as false.
    resultTablesNotKept: z.boolean().nullish(),
    // What the turn did on the way to its answer, as its reply listed them (#7284). Absent on a
    // reply from an API that predates the field, which reads as none.
    steps: z.array(AssistantTurnStepSchema.AssistantTurnStepJSONZ).nullish(),
    // The files the user's message carried (#6504). Absent on a reply from an API that predates
    // the field, which reads as none.
    attachments: z.array(AssistantAttachmentSchema.AssistantAttachmentJSONZ).nullish(),
  });

  public static readonly AssistantConversationTurnZ = z.object({
    sequence: z.number(),
    userMessage: z.string(),
    answerText: z.string(),
    createdAt: z.custom<AHDate>(),
    // Instances, decoded once from JSON this conversation's parse validated (#5992).
    citations: z.array(z.instanceof(AssistantCitation)),
    traceId: z.string().optional(),
    sourceLost: z.boolean().optional(),
    feedback: z.instanceof(AnswerFeedback).optional(),
    resultTablesNotKept: z.boolean().optional(),
    steps: z.array(z.instanceof(AssistantTurnStep)).optional(),
    attachments: z.array(z.instanceof(AssistantAttachment)).optional(),
  });

  public static readonly AssistantConversationJSONZ = z.object({
    conversationId: z.uuid(),
    startedAt: z.iso.datetime({ offset: true }),
    lastMessageAt: z.iso.datetime({ offset: true }),
    turns: z.array(AssistantConversationSchema.AssistantConversationTurnJSONZ),
    // Whether anything was prepared for approval here. The previews themselves are not in
    // `turns` and are not meant to be - but the answers announcing them are, so this is what
    // lets a replayed transcript say that "confirm it below" no longer has anything below it.
    // Absent on a reply from an API that predates the field; absent reads as false, which is
    // the same thing it meant before the field existed.
    hadPreparedActions: z.boolean().nullish(),
  });

  public static readonly AssistantConversationZ = z.object({
    conversationId: z.string(),
    startedAt: z.custom<AHDate>(),
    lastMessageAt: z.custom<AHDate>(),
    turns: z.array(AssistantConversationSchema.AssistantConversationTurnZ),
    hadPreparedActions: z.boolean().optional(),
  });

  public static validate(values: unknown): AssistantConversationParams {
    return parseOrThrow(AssistantConversationSchema.AssistantConversationZ, values, {
      logError: true,
      schemaName: "AssistantConversationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is AssistantConversationParams {
    try {
      AssistantConversationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  /** Validates `json` once and decodes it. */
  public static deserialize(json: AssistantConversationJSON): AssistantConversationParams {
    if (isNullOrUndefined(json)) return json;
    return parseOrThrow(AssistantConversationWireZ, json, {
      logError: true,
      schemaName: "AssistantConversationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
  }
}

/** Validated conversation JSON as params, with each citation decoded without a second parse. */
function decodeConversation(parsed: AssistantConversationJSON): AssistantConversationParams {
  return {
    conversationId: parsed.conversationId,
    startedAt: new AHDate(parsed.startedAt),
    lastMessageAt: new AHDate(parsed.lastMessageAt),
    turns: parsed.turns.map((turn) => ({
      sequence: turn.sequence,
      userMessage: turn.userMessage,
      answerText: turn.answerText,
      createdAt: new AHDate(turn.createdAt),
      citations: turn.citations.map((citation) => new AssistantCitation(AssistantCitationSchema.decodeParsed(citation))),
      traceId: turn.traceId ?? undefined,
      sourceLost: turn.sourceLost ?? undefined,
      feedback: isNullOrUndefined(turn.feedback)
        ? undefined
        : new AnswerFeedback(AnswerFeedbackSchema.decodeParsed(turn.feedback)),
      resultTablesNotKept: turn.resultTablesNotKept ?? undefined,
      steps: (turn.steps ?? []).map((step) => new AssistantTurnStep(AssistantTurnStepSchema.decodeParsed(step))),
      // Validated as part of this conversation's parse, so decoded without a second one.
      attachments: (turn.attachments ?? []).map(
        (attachment) => new AssistantAttachment(AssistantAttachmentSchema.decodeParsed(attachment)),
      ),
    })),
    hadPreparedActions: parsed.hadPreparedActions ?? false,
  };
}

/**
 * The conversation JSON, decoded as it is parsed. The read endpoint's `rawResponseSchema`, so a
 * conversation read back is validated once, where it arrives.
 */
export const AssistantConversationWireZ = AssistantConversationSchema.AssistantConversationJSONZ.transform(decodeConversation);
